import type {
  DefaultWallEndCapSource,
  HalfEdgeToWallEndCapSource,
  LeftMiterExtendWallCapSource,
  RightMiterExtendWallCapSource,
  WallEndCapSourceVertices,
} from "building-types/walls/wallEndsTypes";
import { EndCapSourceType } from "building-types/walls/wallEndsTypes";
import { arrayShift } from "common/utils/arrayShift";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { classifyContainmentVec2PolyLine2Fast } from "geom/containment";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import {
  CurveCurvePointIntersection,
  CurveCurveRangeIntersection,
  intersectLine2Line2,
} from "geom/intersectLine2Line2";
import type { PlanarGraph, PlanarGraphFacePoleOfInaccessibilityPositions } from "geom/planarGraph";
import { planarGraphAdjacentHalfEdge, planarGraphMakeFacePoleOfInaccessibilityPositions } from "geom/planarGraph";
import { polyLine2Area, polyLine2Copy, polyLine2Shift } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import { vec2Dist, vec2DistSquared } from "geom/vec2";

/**
 * When room edge segments are short near concave corners such that wall end cap miters
 * intersect or are arranged in an order that does correspond with the ordering of wall centerlines
 * that created them, it is useful to correct these miters by snapping them to the point of
 * self-intersection created by a polyLine constructed from the left miters of half edges of a planar graph
 * face (or room).
 *
 * This process identifies self-intersections and then remaps miters in the undesired loops to the
 * self-intersection point. In order to properly construct this left miter loop, void wall miters (aka
 * no-op miters were added to the miter list).
 *
 *
 *
 *  1. Wall intersections with short segments at concave corners cause invalid miter locations for Walls I,II, III and IV.
 *     If we create a polyLine from the left miters of the room, we can identify a triangle A,B,C in the room outline that
 *     does not represent the reality of the room.
 *```
 *                 Wall I           Wall II
 *            -----------------------|--/
 *                                   | /| Wall III (very short wall)
 *                                   |/ |
 *                               B---|---
 *                               |\ /|  |
 *                               | \ |  |
 *                               |- -|  |
 *            -------------------A---C  |
 *                               |      | Wall IV
 *                               |      |
 *                               |      |
 *                               |      |
 *                               |      |
 *                               |      |
 *                               |      |
 *                               |      |
 *```
 *  2. We can remap the miters (B and C) to the self-intersection (corresponds with point A) to remove the triangle.
 *```
 *                               B
 *                               |\
 *                               | \     remove triangle artifact
 *                               |  -
 *          +--------------------A---C
 *          |                    |
 *          |                    |
 *          |                    |
 *          |    keep room       |
 *          |                    |
 *          |                    |
 *          |                    |
 *          |                    |
 *          |                    |
 *          +--------------------+
 *```
 *  3. The final miter arrangement produces clean wall geometry that still retains Wall II and Wall III.
 *```
 *                  Wall I           Wall II
 *           -----------------------/--/
 *                                 |  /|   Wall III (very short wall)
 *                                 / / |
 *                                / /  -
 *                               | / -/|
 *                               //-/  |
 *                              |-/    |
 *           -------------------A      |
 *                              |      |   Wall IV
 *                              |      |
 *                              |      |
 *                              |      |
 *                              |      |
 *                              |      |
 *                              |      |
 *                              |      |
 *```
 *
 */

export function repairMitersOnSelfIntersectingRoomEdges(
  wallEndCapSources: HalfEdgeToWallEndCapSource,
  planarGraph: PlanarGraph,
  planarGraphFacePoleOfInaccessibilityPositions?: PlanarGraphFacePoleOfInaccessibilityPositions,
) {
  const { facePositions: facePoleOfInaccessibilityPositions } =
    planarGraphFacePoleOfInaccessibilityPositions ?? planarGraphMakeFacePoleOfInaccessibilityPositions(planarGraph);

  for (let faceIndex = 0; faceIndex < planarGraph.faceHalfEdges.length; faceIndex++) {
    repairFaceMitersOnSelfIntersectingRoomEdges(
      faceIndex,
      wallEndCapSources,
      planarGraph,
      facePoleOfInaccessibilityPositions[faceIndex],
    );

    //TODO: useful for QC'ing left miter polyLine self-intersection solution
    //debugDisplayTrimSelfIntersectionResult(pl, faceIndex);
  }
}

export function repairFaceMitersOnSelfIntersectingRoomEdges(
  face: number,
  wallEndCapSources: HalfEdgeToWallEndCapSource,
  planarGraph: PlanarGraph,
  facePoleOfInaccessibilityPosition: Vec2,
) {
  const sourceVertices = getWallEndCapSourceVertices(face, planarGraph, wallEndCapSources);
  const [testptx, testpty] = facePoleOfInaccessibilityPosition;

  // // for QC'ing result, declare the `pl` property and pass to the `debugDisplayTrimSelfIntersectionResult` function.
  const { vertexRemaps } = polyLine2FromMitersTrimSelfIntersections(sourceVertices, testptx, testpty);
  repairInvalidMitersWithVertexRemaps(face, planarGraph, vertexRemaps, wallEndCapSources);
  repairInvalidMiterExtensions(face, planarGraph, wallEndCapSources);
}

type HalfEdgeRemaps = { [he: number]: Vec2 };
interface PolyLine2FromMitersTrimSelfIntersectionsResult {
  readonly pl: PolyLine2 | null; // this is currently only used for QC'ing the output
  readonly vertexRemaps: HalfEdgeRemaps;
}

function polyLine2FromMitersTrimSelfIntersections(
  vArr: WallEndCapSourceVertices[],
  insideTestPtX: number, // test point x to determine whether polyLine segment editing removed an incorrect loop
  insideTestPtY: number, // test point y to determine whether polyLine segment editing removed an incorrect loop
): PolyLine2FromMitersTrimSelfIntersectionsResult {
  // don't attempt to remap wall end cap miters that don't exist
  if (!vArr.length) {
    return { pl: null, vertexRemaps: {} };
  }

  const vArrLength = vArr.length;

  let leastRemappedVerticesCount = Number.MAX_SAFE_INTEGER;
  let bestPolygon: PolyLine2 | undefined;
  let bestPolygonArea: number = 0;
  let bestShift: number | undefined;
  let bestVertexRemaps: HalfEdgeRemaps | undefined;
  let hasAtLeastOneSolution = false;

  for (let e = 0; e < vArrLength; e++) {
    const vertexRemaps: HalfEdgeRemaps = {};
    let remappedVerticesCount = 0;

    /**
     * depending on which miter-derived vertex is first, the self-intersection loop
     * that is retained might be the small clipped portion rather than the desired whole room.
     * So we shift the array and try them all, but then take the best result, which is
     * quantified by the least remapped vertices with the largest area with a pole of
     * inaccessibility test point from the original room that falls within the original polyLine.
     * */
    vArr = e === 0 ? vArr : arrayShift(vArr, 1);

    for (let i = 0; i < vArrLength; i++) {
      const v1 = vArr[i];
      const v2 = vArr[(i + 1) % vArrLength];

      if (v1.miterType === EndCapSourceType.NO_OP) {
        continue;
      }

      const [ax, ay] = vertexRemaps[v1.halfEdge]
        ? vertexRemaps[v1.halfEdge]
        : [v1.vertices[v1.vertices.length - 2], v1.vertices[v1.vertices.length - 1]];
      const [bx, by] = vertexRemaps[v2.halfEdge] ?? [v2.vertices[0], v2.vertices[1]];

      if (vec2DistSquared(ax, ay, bx, by) < DIST_EPSILON_SQUARED) {
        continue;
      }

      const rangeIntersectionAlreadyRepaired: { [he: number]: boolean } = {};
      let prevZeroLength = false;
      for (let j = i + 1; j < vArrLength; j++) {
        const v3 = vArr[j % vArrLength];
        const v4 = vArr[(j + 1) % vArrLength];
        if (v3.miterType === EndCapSourceType.NO_OP) {
          continue;
        }

        const [cx, cy] = vertexRemaps[v3.halfEdge]
          ? vertexRemaps[v3.halfEdge]
          : [v3.vertices[v3.vertices.length - 2], v3.vertices[v3.vertices.length - 1]];
        const [dx, dy] = vertexRemaps[v4.halfEdge] ?? [v4.vertices[0], v4.vertices[1]];

        // on next edge only allow detection of range intersections
        let onlyAllowRangeIntersection = false;
        if (j === i + 1) {
          onlyAllowRangeIntersection = true;
        } else if (j === (i + 2) % vArrLength && prevZeroLength) {
          onlyAllowRangeIntersection = true;
        }

        prevZeroLength = vec2DistSquared(cx, cy, dx, dy) < 0.01;

        // looping around closed curve
        if (j === (i - 1 + vArrLength) % vArrLength) {
          onlyAllowRangeIntersection = true;
        }

        const int = intersectLine2Line2(ax, ay, bx, by, cx, cy, dx, dy);

        if (!int) {
          continue;
        }

        if (int instanceof CurveCurveRangeIntersection) {
          for (let k = i; k < j; k++) {
            remappedVerticesCount++;
            const current = (k + 1) % vArrLength;
            const { halfEdge } = vArr[current];
            if (!rangeIntersectionAlreadyRepaired[halfEdge]) {
              rangeIntersectionAlreadyRepaired[halfEdge] = true;
              vertexRemaps[halfEdge] = [int.startx, int.starty];
            }
          }
        }

        if (!onlyAllowRangeIntersection && int instanceof CurveCurvePointIntersection) {
          for (let k = i; k < j; k++) {
            remappedVerticesCount++;
            const current = (k + 1) % vArrLength;
            const { halfEdge } = vArr[current];
            vertexRemaps[halfEdge] = [int.x, int.y];
          }
        }
      }
    }

    const plVerts: number[] = [];
    for (const { halfEdge, vertices } of vArr) {
      const result = vertexRemaps[halfEdge];
      if (result) {
        const [rx, ry] = result;
        plVerts.push(rx, ry);
      } else {
        plVerts.push(...vertices);
      }
    }
    const { halfEdge, vertices } = vArr[0];
    const result = vertexRemaps[halfEdge];
    if (result) {
      const [rx, ry] = result;
      plVerts.push(rx, ry);
    } else {
      plVerts.push(...vertices);
    }

    const pl = polyLine2New(plVerts);
    const area = polyLine2Area(pl);

    // ignore the polyLine if it does not contain the test point
    if (area < DIST_EPSILON_SQUARED || !classifyContainmentVec2PolyLine2Fast(insideTestPtX, insideTestPtY, pl)) {
      continue;
    }

    if (remappedVerticesCount <= leastRemappedVerticesCount && area > bestPolygonArea) {
      bestPolygon = polyLine2Copy(pl);
      bestPolygonArea = area;
      leastRemappedVerticesCount = remappedVerticesCount;
      bestShift = e;
      hasAtLeastOneSolution = true;
      bestVertexRemaps = vertexRemaps;
    }

    // if there aren't any remapped vertices, then there's no need to shift to test for the proper loop removal
    if (remappedVerticesCount === 0) {
      break;
    }
  }

  if (!hasAtLeastOneSolution) {
    return { pl: null, vertexRemaps: {} };
  }

  assertIsNotNullOrUndefined(bestPolygon);
  assertIsNotNullOrUndefined(bestVertexRemaps);

  // if bestShift is not 0, then we need to shift the polyLine back to it's original edge order
  if (!!bestShift) {
    // shift the polyLine back to it's original edge order
    bestPolygon = polyLine2Shift(bestPolygon, -1 * bestShift);
  }

  return { pl: polyLine2Copy(bestPolygon), vertexRemaps: bestVertexRemaps };
}

// //Useful for visual debugging
// export function debugDisplayTrimSelfIntersectionResult(pl: Float64Array, faceIndex: string) {
//   if (pl) {
//     debugDisplay({
//       id: faceIndex + "face",
//       objects: [polyLine2ToTHREEDebug(polyLine2Move(pl, 0.01, 0.01), analyticalLineworkPlane)],
//       signals: ["level 1"],
//     });
//   }
// }

// //Useful for visual debugging
// export function debugDisplayMitersAndExtensions(
//   halfEdgeToWallEnd: WallEndCap[],
//   halfEdgeToMiters: HalfEdgeToMiters,
//   rawPlanarGraph: PlanarGraph,
// ) {
//   for (const halfEdge in halfEdgeToWallEnd) {
//     const { lmx, lmy, rmx, rmy } = halfEdgeToMiters[halfEdge];
//     const cap = halfEdgeToWallEnd[halfEdge];
//     const verts = cap.verts;
//     const offsetDifferenceOutline = cap.offsetDifferenceOutline;

//     const colorHex = "#" + ((Math.random() * 0xffffff) << 0).toString(16).padStart(6, "0");
//     const color = new LineBasicMaterial({ color: new Color(colorHex) });

//     const [normx, normy] = planarGraphHalfEdgeNormal(rawPlanarGraph, +halfEdge, true);
//     const [sx, sy] = planarGraphHalfEdgeStart(rawPlanarGraph, +halfEdge);
//     const [ex, ey] = planarGraphHalfEdgeEnd(rawPlanarGraph, +halfEdge);
//     const [tx, ty] = planarGraphHalfEdgeTangent(rawPlanarGraph, +halfEdge);

//     const x = sx + tx * 0.4 + normx * 0.125;
//     const y = sy - 0.09 + ty * 0.4 + normy * 0.125;

//     const circle = arc2ByCenterStartAngle(x, y + 0.09, x + 0.07, y, 2 * Math.PI, undefined, 20, 20);
//     debugDisplay({
//       id: halfEdge + "verts",
//       objects: [polyLine2ToTHREEDebug(circle, analyticalLineworkPlane, color)],
//       signals: ["level 1"],
//     });

//     debugDisplay({
//       id: halfEdge + "halfedge",
//       objects: [
//         polyLine2ToTHREEDebug(
//           polyLine2Move(polyLine2New([sx, sy, ex, ey]), tx * 0.01 + normx * 0.01, ty * 0.01 + normy * 0.01),
//           analyticalLineworkPlane,
//           color,
//         ),
//       ],
//       textObjects: [
//         makeTextLabelSimple(halfEdge.toString(), x, y),
//         makeTextLabelSimple("RM " + halfEdge, rmx + tx * 0.1, rmy + ty * 0.1),
//         makeTextLabelSimple("LM " + halfEdge, lmx + tx * 0.1, lmy + ty * 0.1),
//       ],
//       signals: ["level 1"],
//     });

//     debugDisplay({
//       id: halfEdge + "verts final",
//       objects: [polyLine2ToTHREEDebug(polyLine2New(verts), analyticalLineworkPlane, color)],
//       signals: ["level 1"],
//     });

//     if (offsetDifferenceOutline?.length > 2) {
//       debugDisplay({
//         id: halfEdge + "adjustment",
//         objects: [
//           polyLine2ToTHREEDebug(
//             polyLine2Move(polyLine2New(offsetDifferenceOutline), 0.25, 0.25),
//             analyticalLineworkPlane,
//           ),
//         ],
//         signals: ["level 1"],
//       });
//     }
//   }
// }

/** Collects left miter vertices around planar graph face ordered by half edge around the face */
function getWallEndCapSourceVertices(
  face: number,
  planarGraph: PlanarGraph,
  wallEndCapSources: HalfEdgeToWallEndCapSource,
) {
  const sourceVertices: WallEndCapSourceVertices[] = [];

  for (const halfEdge of planarGraph.faceHalfEdges[face]) {
    const wallEndCapSource = wallEndCapSources[halfEdge];
    const miterType = wallEndCapSource.sourceType;
    switch (miterType) {
      case EndCapSourceType.DEFAULT:
      case EndCapSourceType.FREE_END:
      case EndCapSourceType.RIGHT_MITER_EXTEND: {
        const { lmx, lmy } = wallEndCapSource;
        sourceVertices.push({
          halfEdge,
          miterType,
          vertices: [lmx, lmy],
        });
        break;
      }
      case EndCapSourceType.LEFT_MITER_EXTEND: {
        const { rx, ry, lmx, lmy } = wallEndCapSource;
        sourceVertices.push({
          halfEdge,
          miterType,
          vertices: [rx, ry, lmx, lmy],
        });
        break;
      }
      case EndCapSourceType.NO_OP: {
        const { sx, sy } = wallEndCapSource;
        sourceVertices.push({
          halfEdge,
          miterType,
          vertices: [sx, sy],
        });
        break;
      }
    }
  }
  return sourceVertices;
}

/**
 * fix miter entensions that don't connect to the left or right pair miter due to adjacency to a complex
 * intersection (usually one with multiple short segments and a T intersection with two different collinear wall thicknesses)
 */
function repairInvalidMiterExtensions(
  face: number,
  planarGraph: PlanarGraph,
  wallEndCapSources: HalfEdgeToWallEndCapSource,
) {
  const planarGraphFaceHalfEdges = planarGraph.faceHalfEdges[face];

  for (const he of planarGraphFaceHalfEdges) {
    const leftCap = wallEndCapSources[he];
    const { rx, ry, lmx, lmy } = leftCap as LeftMiterExtendWallCapSource;
    const rightHe = planarGraphAdjacentHalfEdge(
      planarGraphFaceHalfEdges[
        (planarGraphFaceHalfEdges.indexOf(he) - 1 + planarGraphFaceHalfEdges.length) % planarGraphFaceHalfEdges.length
      ],
    );
    const rightCap = wallEndCapSources[rightHe];
    const { rmx, rmy, fillerlx, fillerly } = rightCap as RightMiterExtendWallCapSource;

    /** if the left extended miter doesn't connect to the right miter, remove the extension and set the
     * miter end point on the left to match the right
     */
    if (
      leftCap.sourceType === EndCapSourceType.LEFT_MITER_EXTEND &&
      (Math.abs(rx - rmx) > DIST_EPSILON || Math.abs(ry - rmy) > DIST_EPSILON)
    ) {
      wallEndCapSources[rightHe] = {
        ...rightCap,
        sourceType: EndCapSourceType.DEFAULT,
      } as DefaultWallEndCapSource;

      wallEndCapSources[he] = {
        ...leftCap,
        sourceType: EndCapSourceType.DEFAULT,
        lmx: rmx,
        lmy: rmy,
      } as DefaultWallEndCapSource;
    }

    /** if the right extended miter doesn't connect to the left miter, remove the extension and set the
     * miter end point on the right to match the left
     */
    if (
      rightCap.sourceType === EndCapSourceType.RIGHT_MITER_EXTEND &&
      (Math.abs(fillerlx - lmx) > DIST_EPSILON || Math.abs(fillerly - lmy) > DIST_EPSILON)
    ) {
      wallEndCapSources[rightHe] = {
        ...rightCap,
        sourceType: EndCapSourceType.DEFAULT,
        rmx: lmx,
        rmy: lmy,
      } as DefaultWallEndCapSource;

      wallEndCapSources[he] = {
        ...leftCap,
        sourceType: EndCapSourceType.DEFAULT,
      } as DefaultWallEndCapSource;
    }
  }
}

function repairInvalidMitersWithVertexRemaps(
  face: number,
  planarGraph: PlanarGraph,
  vertexRemaps: HalfEdgeRemaps,
  wallEndCapSources: HalfEdgeToWallEndCapSource,
) {
  const planarGraphFaceHalfEdges = planarGraph.faceHalfEdges[face];

  for (const he in vertexRemaps) {
    const [x, y] = vertexRemaps[he];
    const leftCapSource = wallEndCapSources[he];
    const rightHalfEdge = planarGraphAdjacentHalfEdge(
      planarGraphFaceHalfEdges[
        (planarGraphFaceHalfEdges.indexOf(+he) - 1 + planarGraphFaceHalfEdges.length) % planarGraphFaceHalfEdges.length
      ],
    );
    const rightCapSource = wallEndCapSources[rightHalfEdge];

    if (leftCapSource.sourceType === EndCapSourceType.NO_OP || rightCapSource.sourceType === EndCapSourceType.NO_OP) {
      continue;
    }

    /**
     * this helps protect against situations where remapping jumps to incorrect vertices that are far away.
     * This usually only occurs when short perpendicular segments are used to align the face of two parallel walls with
     * different thicknesses.
     */
    if (vec2Dist(leftCapSource.lmx, leftCapSource.lmy, x, y) > 10) {
      continue;
    }

    if (
      rightCapSource.sourceType === EndCapSourceType.LEFT_MITER_EXTEND ||
      rightCapSource.sourceType === EndCapSourceType.RIGHT_MITER_EXTEND ||
      rightCapSource.rmx === undefined
    ) {
      continue;
    }

    if (
      leftCapSource.sourceType === EndCapSourceType.LEFT_MITER_EXTEND ||
      leftCapSource.sourceType === EndCapSourceType.RIGHT_MITER_EXTEND ||
      leftCapSource.lmx === undefined
    ) {
      continue;
    }

    wallEndCapSources[he] = {
      ...leftCapSource,
      lmx: x,
      lmy: y,
    };

    wallEndCapSources[rightHalfEdge] = {
      ...rightCapSource,
      rmx: x,
      rmy: y,
    };
  }
}
